import React, { useCallback, useMemo, useRef } from 'react';
import { Setting, SettingDetail, SettingInfo, SettingsSectionHeading } from 'components/Setting/style';
import { Divider, ParagraphXSmallRegular } from 'components/Typography';
import { Form } from 'components/Form';
import { InputLabel, InputWrapper } from '@econsult/econsult-ui/dist/components/Input/InputStyles';
import { StyledTextArea } from 'components/CustomClosureMessage/style';
import { Button } from '@econsult/econsult-ui/dist/components/Button';
import { ContactAdminTooltip } from 'components/Tooltip';
import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { ErrorToast } from 'components/Toast';
import { STRINGS } from 'config';
import { saveCustomLandingPageMessageAndVisibility } from 'store/action/landingPageMessage';
import _debounce from 'lodash/debounce';
import { CustomMessageTextareaWrapper } from 'components/CustomLandingPageMessage/style';

const { PENDING, SUCCEEDED, FAILED } = STRINGS.REQUEST_STATUS;

export const CustomLandingPageMessageForm = props => {
  const {
    message,
    showMessage,
    maxMessageLength,
    selectedPractice,
    selectedPracticeHasAdminRights,
    isFormDisabled,
    onChangeMessage,
    isGetRequestPending,
    isSaveRequestPending,
    setSaveRequestStatus,
  } = props;

  const textareaRef = useRef();

  const onSubmit = useCallback(() => {
    if (!selectedPractice || isSaveRequestPending || isGetRequestPending) {
      return null;
    }

    if (message.length > maxMessageLength) {
      return toast(<ErrorToast message="Message is too long." />);
    }

    setSaveRequestStatus(PENDING);

    return saveCustomLandingPageMessageAndVisibility({
      practiceId: selectedPractice.id,
      message: message.trim(),
      showMessage,
    }).then(isSuccess => {
      if (!isSuccess) {
        return setSaveRequestStatus(FAILED);
      }

      return setSaveRequestStatus(SUCCEEDED);
    });
  }, [
    isGetRequestPending,
    isSaveRequestPending,
    maxMessageLength,
    message,
    showMessage,
    selectedPractice,
    setSaveRequestStatus,
  ]);

  const debouncedSubmit = useMemo(() => _debounce(onSubmit, 2000, { leading: true }), [onSubmit]);

  return (
    <Setting>
      <SettingInfo>
        <SettingsSectionHeading data-automation="CustomLandingPageMessageHeading">
          Message on eConsult landing page
        </SettingsSectionHeading>

        <ParagraphXSmallRegular>
          You can use this feature to display a message that all
          patients will see before starting their eConsult. Note that
          patients using the NHS App to submit an eConsult <b>will not</b> see this message.
        </ParagraphXSmallRegular>

        <ParagraphXSmallRegular>
          The content of this message is the responsibility of your organisation, and eConsult cannot assume liability for any outcomes or incidents arising from its use.
        </ParagraphXSmallRegular>
      </SettingInfo>

      <SettingDetail>
        <Form
          action=""
          onSubmit={event => {
            event.preventDefault();
            debouncedSubmit();
          }}
          autocomplete="off"
          data-automation="CustomLandingPageMessageForm"
        >
          <InputWrapper htmlFor="CustomLandingPageMessageInput" state="default">
            <InputLabel data-automation="CustomLandingPageMessageHeading" htmlFor="CustomLandingPageMessageInput">
              Custom message
            </InputLabel>

            <CustomMessageTextareaWrapper>
              <StyledTextArea
                charCounterId="messageCharCounter"
                maxChars={maxMessageLength}
                automationName="CustomLandingPageMessageInput"
                value={message}
                placeholder="Enter your custom message here."
                id="CustomLandingPageMessageInput"
                inputRef={textareaRef}
                multiline
                minRows={4}
                maxRows={6}
                inputProps={{
                  'aria-label': 'Set a custom landing page message',
                  'aria-describedby': 'CustomLandingPageMessageInput-messageCharCounter',
                  readOnly: isGetRequestPending,
                }}
                fullWidth
                onChange={onChangeMessage}
                error={message.length > maxMessageLength}
                disabled={isFormDisabled}
              />

              <Divider />
            </CustomMessageTextareaWrapper>
          </InputWrapper>

          <Button
            size="small"
            type="submit"
            data-automation="SaveCustomLandingPageMessageButton"
            disabled={isFormDisabled}
          >
            Save message

            <ContactAdminTooltip show={!selectedPracticeHasAdminRights} automationLabel="SaveCustomLandingPageMessageDisabledTooltip" />
          </Button>
        </Form>
      </SettingDetail>
    </Setting>
  );
};
